import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import type { Rect } from '../types.js';

export interface BoundsOptions {
  threshold?: number;
  /** 3 reads alpha (RGBA images), 0 reads red (single-channel mattes). */
  channel?: 0 | 3;
  /** Extra margin added on every side, clamped to the image. */
  padding?: number;
}

/**
 * Bounding box of everything above an alpha threshold, or `null` if nothing is.
 *
 * Drives auto-trim on export. Print services scale the uploaded file into the
 * print area, so a transparent margin silently shrinks the artwork - trimming is
 * not cosmetic here.
 *
 * This one op does read back from the GPU (16 bytes), so it awaits a submit.
 */
export async function computeBounds(
  ctx: GpuContext,
  src: GpuImage,
  options: BoundsOptions = {},
): Promise<Rect | null> {
  const bounds = ctx.device.createBuffer({
    label: 'bbox',
    size: 16,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
  });
  const readback = ctx.device.createBuffer({
    label: 'bbox-read',
    size: 16,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });

  // Seed min with the image size and max with 0 so an empty result stays
  // detectable as min > max.
  ctx.queue.writeBuffer(bounds, 0, new Uint32Array([src.width, src.height, 0, 0]));

  ctx.dispatch(
    {
      module: 'bbox.wgsl',
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'buffer', access: 'read-write' },
        { kind: 'uniform' },
      ],
    },
    [
      src,
      bounds,
      ctx.uniform(
        new Struct(16)
          .i(0, src.width, src.height)
          .f(8, options.threshold ?? 0.02)
          .u(12, options.channel ?? 3).bytes,
      ),
    ],
    [workgroupCount(src.width, 8), workgroupCount(src.height, 8)],
    'bbox',
  );

  ctx.endPass();
  ctx.encoder().copyBufferToBuffer(bounds, 0, readback, 0, 16);
  ctx.flush();

  try {
    await readback.mapAsync(GPUMapMode.READ);
    const values = new Uint32Array(readback.getMappedRange().slice(0));
    readback.unmap();

    const [minX, minY, maxX, maxY] = [values[0]!, values[1]!, values[2]!, values[3]!];
    if (minX > maxX || minY > maxY) return null;

    const pad = Math.max(0, Math.round(options.padding ?? 0));
    const x = Math.max(0, minX - pad);
    const y = Math.max(0, minY - pad);
    return {
      x,
      y,
      width: Math.min(src.width - 1, maxX + pad) - x + 1,
      height: Math.min(src.height - 1, maxY + pad) - y + 1,
    };
  } finally {
    bounds.destroy();
    readback.destroy();
  }
}
